import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { generateKey } from '../../utils/key'

const BLOB_COLORS = ['var(--accent-teal)', 'var(--accent-orange)', 'var(--activity-intake)', '#f43f5e', '#38bdf8']
const BLOB_ANIMS = ['blob-1', 'blob-2', 'blob-3', 'blob-4']

const NAV = [
  { to: '/about', label: 'About' },
  { to: '/how-it-works', label: 'How it works' },
  { to: '/features', label: 'Features' },
  { to: '/security', label: 'Security' },
  { to: '/contact', label: 'Contact' },
]

interface Blob {
  size: number
  color: string
  top: string
  left: string
  anim: string
  delay: number
}

const rand = (min: number, max: number) => Math.random() * (max - min) + min

// 1–2 blobs above the fold left, 1–2 right, plus a couple lower; fresh each load.
function randomBlobs(): Blob[] {
  const colors = [...BLOB_COLORS].sort(() => Math.random() - 0.5)
  let ci = 0
  const make = (top: string, left: string): Blob => ({
    size: Math.round(rand(280, 540)),
    color: colors[ci++ % colors.length],
    top,
    left,
    anim: BLOB_ANIMS[Math.floor(Math.random() * BLOB_ANIMS.length)],
    delay: -Math.round(rand(0, 22)),
  })
  const blobs: Blob[] = []
  const leftCount = Math.random() < 0.5 ? 1 : 2
  const rightCount = Math.random() < 0.5 ? 1 : 2
  for (let i = 0; i < leftCount; i++) blobs.push(make(`${rand(-8, 42)}vh`, `${rand(-8, 24)}%`))
  for (let i = 0; i < rightCount; i++) blobs.push(make(`${rand(-8, 42)}vh`, `${rand(76, 108)}%`))
  for (let i = 0; i < 2; i++) blobs.push(make(`${rand(58, 94)}%`, `${rand(5, 92)}%`))
  return blobs
}

export function Logo({ className = '', onClick }: { className?: string; onClick?: () => void }) {
  return (
    <Link to="/" onClick={onClick} className={`font-display ${className}`} aria-label="WLNS.app home">
      <span className="font-extrabold uppercase tracking-tight text-[var(--text-primary)]">WLNS</span>
      <span className="text-[0.8em] font-light text-[var(--text-secondary)]">.app</span>
    </Link>
  )
}

export default function PublicLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [blobs] = useState(randomBlobs)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const startFree = () => navigate(`/u/${generateKey()}?welcome=1`)

  return (
    <div className="landing grain relative flex min-h-screen w-full flex-col overflow-hidden text-[var(--text-primary)]">
      {/* Background blobs */}
      <div className="pointer-events-none absolute inset-0 z-0">
        {blobs.map((b, i) => (
          <div
            key={i}
            className={`blob ${b.anim}`}
            style={{ background: b.color, width: b.size, height: b.size, top: b.top, left: b.left, transform: 'translate(-50%, -50%)', animationDelay: `${b.delay}s` }}
          />
        ))}
      </div>

      {/* Header: hamburger (left) + logo + CTA */}
      <header className="relative z-20 border-b border-[var(--border)]/60 bg-[var(--bg-primary)]/40 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-3.5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="rounded-lg p-2 text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-card)] hover:text-[var(--text-primary)]"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Logo className="text-xl sm:text-2xl" />
          </div>
          <button onClick={startFree} className="btn btn-primary px-4 py-2 text-sm font-semibold">
            Start free
          </button>
        </div>
      </header>

      {/* Slide-in drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="drawer-backdrop absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <nav className="drawer-panel absolute left-0 top-0 flex h-full w-72 max-w-[82vw] flex-col border-r border-[var(--border)] bg-[var(--bg-secondary)] p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <Logo className="text-xl" onClick={() => setMenuOpen(false)} />
              <button
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                className="rounded-lg p-1.5 text-[var(--text-secondary)] hover:bg-[var(--bg-card)] hover:text-[var(--text-primary)]"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex flex-col">
              {NAV.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-lg px-3 py-3 text-base text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-card)]"
                >
                  {l.label}
                </Link>
              ))}
            </div>
            <div className="mt-auto border-t border-[var(--border)] pt-4">
              <button
                onClick={() => {
                  setMenuOpen(false)
                  startFree()
                }}
                className="btn btn-primary w-full"
              >
                Start your log — free
              </button>
              <Link
                to="/recover"
                onClick={() => setMenuOpen(false)}
                className="mt-3 block text-center text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                Recover your key
              </Link>
            </div>
          </nav>
        </div>
      )}

      <main className="relative z-10 flex-1">{children}</main>

      <Footer />
    </div>
  )
}

function Footer() {
  const FooterLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
    <li>
      <Link to={to} className="text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]">
        {children}
      </Link>
    </li>
  )
  return (
    <footer className="relative z-10 mt-20 border-t border-[var(--border)] bg-[var(--bg-secondary)]/70 backdrop-blur-sm">
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-4">
          <div className="md:col-span-2">
            <Logo className="text-2xl" />
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-[var(--text-secondary)]">
              Private fitness &amp; nutrition tracking. No account, no email — just your key.
            </p>
          </div>
          <div>
            <h4 className="font-display text-sm font-bold">Product</h4>
            <ul className="mt-3 space-y-2 text-sm">
              <FooterLink to="/features">Features</FooterLink>
              <FooterLink to="/how-it-works">How it works</FooterLink>
              <FooterLink to="/security">Security</FooterLink>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-sm font-bold">Company</h4>
            <ul className="mt-3 space-y-2 text-sm">
              <FooterLink to="/about">About</FooterLink>
              <FooterLink to="/contact">Contact</FooterLink>
            </ul>
          </div>
        </div>
        <div className="mt-8 flex flex-col gap-2 border-t border-[var(--border)] pt-6 text-xs text-[var(--text-secondary)] sm:flex-row sm:items-center sm:justify-between">
          <span>© 2026 wlns.app · Private by design — estimates, not medical advice.</span>
          <a
            href="https://sourcecode.com.au"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 transition-colors hover:text-[var(--text-primary)]"
          >
            <span>Another app built by</span>
            <svg viewBox="0 0 200 32" className="h-4" aria-label="source[code]">
              <text
                x="100"
                y="24"
                textAnchor="middle"
                fill="currentColor"
                fontFamily="ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace"
                fontSize="24"
                fontWeight="700"
              >
                source<tspan fill="#3b82f6">[</tspan>code<tspan fill="#3b82f6">]</tspan>
              </text>
            </svg>
          </a>
        </div>
      </div>
    </footer>
  )
}
